// src/lib/http.js
import axios from "axios";

// --- პატარა ჰელპერები
const sleep = (ms) => new Promise(res => setTimeout(res, ms));
const rand = (min, max) => Math.random() * (max - min) + min;
const genIdemKey = () =>
  (crypto?.randomUUID?.() || Math.random().toString(36).slice(2)) + Date.now();

const DEFAULTS = {
  baseURL: "/",                 // ჩაანაცვლე თუ გინდა
  withCredentials: true,
  timeout: 15_000,              // 15s per attempt
  maxRetries: 3,                // საერთო მაქს. ცდები (ერთი პირველადი + 3 retry)
  retryMethods: ["get", "head", "options"], // უსაფრთხოები by default
  retryOnStatus: [500, 502, 503, 504, 507, 429, 508],
  baseDelay: 400,               // ms (exponential backoff base)
  maxDelay: 6_000,              // ჭერი
  addIdempotencyForWrites: true // POST/PUT/PATCH-ზე დავუმატოთ key რომ დუბლირება არ მოხდეს
};

// Exponential backoff + jitter
function calcDelay(attempt, baseDelay, maxDelay) {
  const expo = baseDelay * Math.pow(2, attempt);     // 0→base, 1→2x, 2→4x ...
  const jitter = rand(0.4, 1.0);                     // მცირე შერყევა
  return Math.min(Math.floor(expo * jitter), maxDelay);
}

// ვამუშავებთ Retry-After ჰედერს სერვერიდან
function parseRetryAfter(retryAfterHeader) {
  if (!retryAfterHeader) return null;
  const n = Number(retryAfterHeader);
  if (!Number.isNaN(n)) return Math.max(0, Math.floor(n * 1000));
  const d = new Date(retryAfterHeader);
  const delta = d.getTime() - Date.now();
  return delta > 0 ? delta : null;
}

export function createApi(custom = {}) {
  const cfg = { ...DEFAULTS, ...custom };
  const api = axios.create({
    baseURL: cfg.baseURL,
    withCredentials: cfg.withCredentials,
    timeout: cfg.timeout,
  });

  // --- Request interceptor
  api.interceptors.request.use((req) => {
    req.__retryCount = req.__retryCount ?? 0;
    req.__startTs = Date.now();

    // AbortController-ის ბაზაზე per-attempt timeout (axios timeout-იც გვაქ)
    if (!req.signal && typeof AbortController !== "undefined") {
      const controller = new AbortController();
      req.signal = controller.signal;
      const t = setTimeout(() => controller.abort(), cfg.timeout);
      // დავიმახსოვროთ რომ response-ზე გავთიშოთ
      req.__abortTimeout = t;
      req.__abortController = controller;
    }

    // POST/PUT/PATCH-ზე ჩავურთოთ იდემპოტენცია (თუ გვინდა)
    const method = (req.method || "get").toLowerCase();
    const isWrite = ["post", "put", "patch", "delete"].includes(method);
    if (isWrite && cfg.addIdempotencyForWrites && !req.headers?.["X-Idempotency-Key"]) {
      req.headers = req.headers || {};
      req.headers["X-Idempotency-Key"] = genIdemKey();
    }

    return req;
  });

  // --- Response interceptor (success)
  api.interceptors.response.use(
    (res) => {
      // გავასუფთავოთ per-attempt აბორტის ტაიმერი
      if (res.config?.__abortTimeout) clearTimeout(res.config.__abortTimeout);
      return res;
    },

    // --- Response interceptor (error + retry logic)
    async (error) => {
      const cfgReq = error.config || {};
      if (cfgReq.__abortTimeout) clearTimeout(cfgReq.__abortTimeout);

      const method = (cfgReq.method || "get").toLowerCase();
      const status = error.response?.status;
      const isNetwork = !!error.isAxiosError && !error.response; // DNS / CORS / offline

      // მოცდის დრო Retry-After-დან (არსებობის შემთხვევაში)
      const retryAfterMs = parseRetryAfter(error.response?.headers?.["retry-after"]);

      // გადავწყვიტოთ მაინც retry?
      const canRetryMethod =
        cfg.retryMethods.includes(method) ||
        // ჩანართი: წერით მეთოდზეც ვცადოთ retry, თუ იდემპოტენცია ჩართულია
        (cfg.addIdempotencyForWrites && ["post", "put", "patch", "delete"].includes(method));

      const shouldRetryStatus = status ? cfg.retryOnStatus.includes(status) : false;
      const shouldRetry = canRetryMethod && (isNetwork || shouldRetryStatus);

      if (!shouldRetry) {
        return Promise.reject(error);
      }

      // მაქსიმალურ ცდებს რომ არ გაცდეს
      const attempt = (cfgReq.__retryCount || 0);
      if (attempt >= cfg.maxRetries) {
        return Promise.reject(error);
      }

      // თუ ოფლაინია — დაველოდოთ "online" ღონისძიებას და მერე ვცადოთ
      if (typeof window !== "undefined" && navigator && navigator.onLine === false) {
        await new Promise((resolve) => {
          const onBack = () => {
            window.removeEventListener("online", onBack);
            resolve();
          };
          window.addEventListener("online", onBack, { once: true });
        });
      }

      // დელეი Retry-After-ით ან backoff-ით
      const delay = retryAfterMs ?? calcDelay(attempt, cfg.baseDelay, cfg.maxDelay);
      cfgReq.__retryCount = attempt + 1;

      await sleep(delay);

      // ახალ აბორტკონტროლერს მივანიჭებთ შემდეგ ცდაზე
      if (typeof AbortController !== "undefined") {
        const controller = new AbortController();
        cfgReq.signal = controller.signal;
        const t = setTimeout(() => controller.abort(), cfg.timeout);
        cfgReq.__abortTimeout = t;
        cfgReq.__abortController = controller;
      }

      return api(cfgReq);
    }
  );

  return api;
}

// აპში გამოსაყენებლად — ერთი shared instance
export const api = createApi();
